import { useMemo, useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, Typography, Loader, Button } from '@lucid/lego';
import { useUserIdentity } from '@lucid/portal-core';
import { TEST_DRIVE_BOOKINGS_LABELS_ID } from '../../../config/constants';
import { useStore } from '../../../store/store';
import { useLabels } from '../../../hooks/useLabels';
import { useLocaleNavigate } from '../../../hooks/useLocaleNavigate';
import { resolveEffectiveLocale } from '../../../utils/tools';
import { useBookingSearchTable } from './hooks/useBookingSearchTable';
import { applyBookingFiltersToSearchParams, parseBookingFiltersFromSearchParams } from './utils';
import Table from './Table/Table';
import styles from './TestDriveBookings.module.scss';

/**
 * Prospect Desk / Test Drive Bookings — agency-wide, cross-prospect view of all test-drive bookings.
 * Supports searching/filtering by status, vehicle model, VIN and appointment date range, and
 * can be pre-filtered to a single prospect via the `?prospectId=` query param (used by the
 * "View All Bookings" CTA on a prospect's Test Drive tab).
 *
 * @component
 * @returns {JSX.Element} The Test Drive Bookings page
 */
const TestDriveBookings: React.FC = () => {
  const { userIdentity } = useUserIdentity();
  const theme = useStore((state) => state.theme);
  const locale = useStore((state) => state.locale);
  const language = useStore((state) => state.language);
  const navigate = useLocaleNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialProspectId = searchParams.get('prospectId') ?? undefined;

  const [initialFilters] = useState(() => parseBookingFiltersFromSearchParams(searchParams));

  // Labels use the language-aware locale (same resolution as Prospects) so an English language
  // override doesn't fall back to the regional locale's translations (e.g. German "Interessent").
  const effectiveLocale = resolveEffectiveLocale(language, userIdentity?.countryCode, locale);

  const {
    data: labels,
    isLoading: labelsLoading,
    error: labelsError,
  } = useLabels({
    id: TEST_DRIVE_BOOKINGS_LABELS_ID,
    locale: effectiveLocale,
    countryCode: userIdentity?.countryCode,
    language: null,
  });

  const {
    data,
    totalCount,
    isLoading,
    isFetching,
    error,
    currentPage,
    pageSize,
    filters,
    prospectId,
    searchText,
    sortField,
    sortOrder,
    onPageChange,
    onPageSizeChange,
    onFiltersChange,
    onSearchChange,
    onClearFilters,
    onSort,
  } = useBookingSearchTable({ initialProspectId, initialFilters });

  // Mirror the active filters onto the URL (replace, so Back isn't polluted). The latest params are
  // read through a ref so an external URL change isn't clobbered until the user changes a filter.
  const searchParamsRef = useRef(searchParams);
  searchParamsRef.current = searchParams;
  useEffect(() => {
    const next = applyBookingFiltersToSearchParams(searchParamsRef.current, filters, prospectId);
    if (next.toString() !== searchParamsRef.current.toString()) {
      setSearchParams(next, { replace: true });
    }
  }, [filters, prospectId, setSearchParams]);

  const invertedTheme = useMemo(() => (theme === 'dark' ? 'light' : 'dark'), [theme]);

  const handleBackToProspects = useCallback(() => navigate('/prospect-desk/prospects'), [navigate]);

  const BackButton = (
    <Box mb={16}>
      <Button
        type="tertiary"
        color="neutral"
        theme={theme}
        dataTestId="test-drive-bookings-back-button"
        gaComponent="test-drive-bookings"
        label={labels?.['page.backToProspects'] ?? 'Back to Prospects'}
        iconLeft={{ category: 'arrows', name: 'arrow-left' }}
        handleClick={handleBackToProspects}
      />
    </Box>
  );

  if (labelsLoading || isLoading) {
    return (
      <Box>
        {BackButton}
        <Typography variant="headline" size="small" color="primary-1" theme={theme}>
          {labels?.['title'] ?? 'Test Drive Bookings'}
        </Typography>
        <Typography variant="body" size="medium" color="gray-3" theme={theme}>
          {labels?.['description'] ?? 'View and manage test-drive bookings across all prospects'}
        </Typography>
        <Box className={styles.loaderContainer}>
          <Loader size={48} theme={theme} />
        </Box>
      </Box>
    );
  }

  if (labelsError) {
    return (
      <Box>
        {BackButton}
        <Box pt={24}>
          <Typography variant="body" size="large" theme={theme} color="error-1">
            {labels?.error_loading_labels}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      {BackButton}
      <Typography variant="headline" size="small" color="primary-1" theme={theme}>
        {labels?.['title'] ?? 'Test Drive Bookings'}
      </Typography>
      <Typography variant="body" size="medium" color="gray-3" theme={theme}>
        {labels?.['description'] ?? 'View and manage test-drive bookings across all prospects'}
      </Typography>
      <Box pt={24} className={styles.loadingContainer}>
        {isFetching && (
          <Box className="fetchingOverlay">
            <Loader size={48} theme={invertedTheme} />
          </Box>
        )}
        <Table
          theme={theme}
          data={error ? [] : data}
          totalCount={error ? 0 : totalCount}
          bookingError={Boolean(error)}
          currentPage={currentPage}
          pageSize={pageSize}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
          labels={labels || {}}
          filters={filters}
          onFiltersChange={onFiltersChange}
          onClearFilters={onClearFilters}
          searchText={searchText}
          onSearchChange={onSearchChange}
          sortColumn={sortField}
          sortDirection={sortOrder}
          onSort={onSort}
          locale={locale}
          labelLocale={effectiveLocale}
        />
      </Box>
    </Box>
  );
};

export default TestDriveBookings;
